import dayjs from "dayjs";
import timeUtils from "../../../../utils/timeUtils";
import ConstraintsService from "./ConstraintsService";
import {
    evaluateAircraftAvailability,
    getAircraftAvailabilityWindows,
    normalizeAircraftRegistration,
} from "../../shared/aircraftAvailabilityConstraints";
import { getCurrentBlockTimeMinutes, truncateIsoToMinute } from "../../../../utils/staBlockTimeAlignment";
import { getAirportConstraint, normalizeAirportCode } from "../../../../utils/airportCodeUtils";
import { resolveRequiredMinGroundTime, buildMissingMinGroundTimeError } from "../../../../utils/minGroundTimeUtils";

function restoreOriginalPlanTimes(currentFlight, originalFlight) {
    if (!originalFlight) return currentFlight;
    currentFlight.std = originalFlight.std;
    currentFlight.sta = originalFlight.sta;
    currentFlight.flight_date = originalFlight.flight_date;
    currentFlight.block_time_minutes = originalFlight.block_time_minutes;
    currentFlight.min_block_time_minutes = originalFlight.min_block_time_minutes;
    currentFlight.flight_route_block_time_minutes = originalFlight.flight_route_block_time_minutes;
    if (originalFlight.time_adjustments) {
        currentFlight.time_adjustments = originalFlight.time_adjustments;
    } else {
        delete currentFlight.time_adjustments;
    }
    return currentFlight;
}

/**
 * 宵禁结束后的缓冲分钟数（重算落点 = 宵禁结束 + 缓冲的精确值，不取整）。
 * 重算的处理范围是 [宵禁开始, 宵禁结束 + 缓冲)：缓冲带内的时间合法、不算违规，
 * 但重算会把它挪到"结束 + 缓冲"。
 */
const CURFEW_BUFFER_MINUTES = timeUtils.CURFEW_END_BUFFER_MINUTES;

/**
 * 航班时间计算服务
 * 用于处理航班时间计算、检查宵禁和约束条件等功能
 */
class TimeCalculationService {
    /**
     * 自动计算航班序列的起飞和到达时间
     * @param {Array} flights - 航班列表
     * @param {number} startIndex - 开始计算的航班索引（该班时间不动，从下一班起算）
     * @param {Object} options - 计算选项
     * @param {number} [options.endIndex] - 可选；算到该下标（含）后停止，不改其后航班。未传则算到表末
     * @returns {Object} { flights, discontinuity?, caughtUp?, error? }
     */
    static calculateFlightTimes(flights, startIndex, options) {
        const {
            airportConstraints,
            considerMinGroundTime = true,
            considerCurfew = true,
            considerTransportDemand = false,
            transportationDemands = [],
            airportsMap = {},
            timeZone = "UTC",
            ignoreFlightContinuity = false, // 是否忽略航班连续性
            minimumImpactMode = false, // 最小影响模式：如果计算出的新时间已经赶上或早于原有时间，则停止后续计算
            originalFlights = [], // 原始航班列表，用于最小影响模式比较
            aircraftAvailabilityWindowsByRegistration = {},
            aircraftAvailabilityQueryStart = null,
            aircraftAvailabilityQueryEnd = null,
            endIndex = null, // 算到指定结束下标（含）就停；null 表示算到表末
        } = options || {};

        const lastIndex = flights && flights.length > 0 ? flights.length - 1 : -1;
        const effectiveEndIndex = endIndex == null ? lastIndex : Math.min(endIndex, lastIndex);

        if (!flights || flights.length === 0 || startIndex >= effectiveEndIndex || startIndex >= lastIndex) {
            // 没有可计算的航班：与其余分支保持一致的 { flights } 结构，避免调用方按 result.flights 解析时报错
            return { flights: flights || [] };
        }

        // 创建航班副本以避免直接修改原始数据
        const updatedFlights = [...flights];

        // 从startIndex+1开始计算，到 effectiveEndIndex（含）为止
        for (let i = startIndex + 1; i <= effectiveEndIndex; i++) {
            const prevFlight = updatedFlights[i - 1];
            const currentFlight = { ...updatedFlights[i] };
            const originalFlight = minimumImpactMode && originalFlights.length > 0 ? originalFlights.find((f) => f.id === currentFlight.id) : null;

            const prevArr = normalizeAirportCode(prevFlight.arrival_airport);
            const currDep = normalizeAirportCode(currentFlight.departure_airport);
            const isSameAircraft = prevFlight.aircraft_reg === currentFlight.aircraft_reg;
            // 机场码缺失也按断链处理：静默跳过会让该航班既不参与计算又不给任何提示
            const isConnected = Boolean(prevArr) && prevArr === currDep;

            // 检查航班连续性 - 同一飞机但出发地不等于上一航班到达地
            if (!ignoreFlightContinuity && isSameAircraft && !isConnected) {
                // 返回连续性中断信息，让调用者决定如何处理
                return {
                    flights: updatedFlights.slice(0, i), // 返回计算到的航班
                    discontinuity: {
                        index: i,
                        prevFlight,
                        currentFlight,
                    },
                };
            }

            // 只有相同飞机注册号且出发地等于上一航班到达地的航班才进行计算
            if (isSameAircraft && isConnected && prevFlight.sta) {
                // 使用UTC完整时间计算
                const prevStaTime = dayjs.utc(prevFlight.sta);

                if (prevStaTime.isValid()) {
                    // 计算初始STD时间 - 使用最小过站时间
                    let initialStdTime = prevStaTime;
                    if (considerMinGroundTime) {
                        // 只认到达站在机场主数据里的明确配置（含 0 分钟）。航班上存着的
                        // min_ground_time_minutes 不参与取值，缺配置直接拦住——
                        // 不得用“比大小取较大值”或默认 60 分钟排出一版 1 小时过站
                        const requiredMinGroundTime = resolveRequiredMinGroundTime(prevFlight, airportConstraints);
                        if (requiredMinGroundTime === null) {
                            return {
                                flights,
                                error: buildMissingMinGroundTimeError(prevFlight),
                            };
                        }

                        initialStdTime = prevStaTime.add(requiredMinGroundTime, "minute");
                    }

                    // 创建需要考虑的约束列表
                    const constraints = [];

                    // 1. 添加宵禁约束
                    if (considerCurfew) {
                        constraints.push({
                            type: "curfew",
                            enabled: true,
                        });
                    }

                    if (
                        normalizeAircraftRegistration(currentFlight.aircraft_reg)
                        && aircraftAvailabilityQueryStart
                        && aircraftAvailabilityQueryEnd
                    ) {
                        constraints.push({
                            type: "aircraftAvailability",
                            enabled: true,
                        });
                    }

                    // 2. 添加运输需求约束（周几和首选时间）
                    if (considerTransportDemand && transportationDemands.length > 0) {
                        // 查找与当前航班匹配的运输需求
                        const matchingDemand = transportationDemands.find((demand) => demand.origin === currentFlight.departure_airport);

                        if (matchingDemand) {
                            // 如果有首选日期，添加周几约束
                            if (matchingDemand.preferred_days && matchingDemand.preferred_days.length > 0) {
                                constraints.push({
                                    type: "weekday",
                                    allowedDays: matchingDemand.preferred_days.map((day) => parseInt(day)), // 转换为数字
                                    explanation: `运输需求要求 ${currentFlight.departure_airport}-${currentFlight.arrival_airport} 在特定星期运行`,
                                });
                            }

                            // 如果有首选起飞时间，添加时间约束
                            if (matchingDemand.preferred_time) {
                                constraints.push({
                                    type: "preferredTime",
                                    preferredTime: matchingDemand.preferred_time,
                                    origin: matchingDemand.origin,
                                    explanation: `运输需求要求 ${currentFlight.departure_airport} 在 ${matchingDemand.preferred_time} 附近起飞`,
                                });
                            }
                        }
                    }

                    const blockMinutes = getCurrentBlockTimeMinutes(currentFlight);
                    if (blockMinutes == null) {
                        currentFlight.std = initialStdTime.format("YYYY-MM-DDTHH:mm:ss") + "Z";
                        currentFlight.flight_date = currentFlight.std;
                        if (minimumImpactMode && originalFlight && originalFlight.std) {
                            const calculatedStdTime = dayjs.utc(currentFlight.std);
                            const originalStdTime = dayjs.utc(originalFlight.std);
                            if (calculatedStdTime.isSame(originalStdTime) || calculatedStdTime.isBefore(originalStdTime)) {
                                restoreOriginalPlanTimes(currentFlight, originalFlight);
                                return {
                                    flights: updatedFlights.slice(0, i),
                                    caughtUp: {
                                        index: i,
                                        flight: currentFlight,
                                        message: `航班（${currentFlight.flight_number}，${currentFlight.departure_airport}-${currentFlight.arrival_airport}）已赶上原计划进度，后续航班无需调整`,
                                    },
                                };
                            }
                        }
                        updatedFlights[i] = currentFlight;
                        continue;
                    }

                    // 查找下一个满足所有约束的起飞时间和到达时间
                    const result = this.findNextValidTime(
                        initialStdTime.toDate(),
                        currentFlight.departure_airport,
                        currentFlight.arrival_airport,
                        blockMinutes,
                        constraints,
                        {
                            airportConstraints,
                            airportsMap,
                            timeZone,
                            aircraft_reg: currentFlight.aircraft_reg, // 传递飞机注册号
                            aircraftAvailabilityWindowsByRegistration,
                            aircraftAvailabilityQueryStart,
                            aircraftAvailabilityQueryEnd,
                        },
                    );

                    if (result.error) {
                        return {
                            flights,
                            error: result.error,
                        };
                    }

                    // 直接使用返回的起飞和到达时间
                    currentFlight.std = result.stdTime;
                    currentFlight.sta = result.staTime;

                    // 同时更新航班日期，确保与STD完全一致(包括时间部分) - 符合新规范
                    currentFlight.flight_date = currentFlight.std;

                    // 保存调整记录
                    if (result.adjustments && result.adjustments.length > 0) {
                        currentFlight.time_adjustments = result.adjustments;
                    }

                    // 最小影响模式：检查计算后的时间是否已经追上或早于原始时间
                    if (minimumImpactMode && originalFlight && originalFlight.std) {
                        const calculatedStdTime = dayjs.utc(currentFlight.std);
                        const originalStdTime = dayjs.utc(originalFlight.std);

                        // 如果计算后的时间小于等于原始时间，进入"追平"判断；
                        // 追平成立前还要确认：这一班原时间不在宵禁或缓冲带内，链条后面也没有这样的航班
                        if (calculatedStdTime.isSame(originalStdTime) || calculatedStdTime.isBefore(originalStdTime)) {
                            // 1) 这一班自己的原时间落在 [宵禁开始, 结束+缓冲) 内：从原时间出发重新找落点（同样带缓冲），继续处理下一班
                            if (
                                (considerCurfew && this.isFlightTimeInCurfewOrBuffer(originalFlight, airportConstraints))
                                || this.isFlightInAircraftAvailability(
                                    originalFlight,
                                    aircraftAvailabilityWindowsByRegistration,
                                )
                            ) {
                                const reResult = this.findNextValidTime(dayjs.utc(originalFlight.std).toDate(), currentFlight.departure_airport, currentFlight.arrival_airport, blockMinutes, constraints, {
                                    airportConstraints,
                                    airportsMap,
                                    timeZone,
                                    aircraft_reg: currentFlight.aircraft_reg,
                                    aircraftAvailabilityWindowsByRegistration,
                                    aircraftAvailabilityQueryStart,
                                    aircraftAvailabilityQueryEnd,
                                });
                                if (reResult.error) {
                                    return {
                                        flights,
                                        error: reResult.error,
                                    };
                                }
                                currentFlight.std = reResult.stdTime;
                                currentFlight.sta = reResult.staTime;
                                currentFlight.flight_date = currentFlight.std;
                                if (reResult.adjustments && reResult.adjustments.length > 0) {
                                    currentFlight.time_adjustments = reResult.adjustments;
                                } else if (originalFlight.time_adjustments) {
                                    // 重新落点没有产生调整时，须清掉第一次计算写入的记录，
                                    // 否则悬浮说明与已被 reResult 覆盖的时间对不上（还原风格与分支 2 一致）
                                    currentFlight.time_adjustments = originalFlight.time_adjustments;
                                } else {
                                    delete currentFlight.time_adjustments;
                                }
                                updatedFlights[i] = currentFlight;
                                continue;
                            }

                            // 2) 链条后面（仅同一 aircraft_reg，旧版列表多机混排）还有原时间在宵禁或缓冲带内的航班：
                            //    这一班保持原时间不动，继续往后处理，不因"追平"提前结束。
                            //    注意此前 currentFlight 已被写入计算值，须显式还原为原值再写回
                            if (
                                (considerCurfew && this.hasDownstreamCurfewOrBufferConflict(updatedFlights, originalFlights, i + 1, currentFlight.aircraft_reg, airportConstraints, effectiveEndIndex))
                                || this.hasDownstreamAircraftAvailabilityConflict(
                                    updatedFlights,
                                    originalFlights,
                                    i + 1,
                                    currentFlight.aircraft_reg,
                                    aircraftAvailabilityWindowsByRegistration,
                                    effectiveEndIndex,
                                )
                            ) {
                                restoreOriginalPlanTimes(currentFlight, originalFlight);
                                updatedFlights[i] = currentFlight;
                                continue;
                            }

                            // 3) 真正追平：本班保持原时间（切片不含本班），返回"已赶上进度"。
                            //    切片包含此前被重新落点的航班
                            return {
                                flights: updatedFlights.slice(0, i),
                                caughtUp: {
                                    index: i,
                                    flight: currentFlight,
                                    message: `航班（${currentFlight.flight_number}，${currentFlight.departure_airport}-${currentFlight.arrival_airport}）已赶上原计划进度，后续航班无需调整`,
                                },
                            };
                        }
                    }
                }
            }

            // 更新当前航班
            updatedFlights[i] = currentFlight;
        }

        // 返回更新后的航班列表
        return { flights: updatedFlights };
    }

    /**
     * 查找下一个满足所有约束的有效时间
     * @param {Date} initialTime - 初始时间
     * @param {string} depAirport - 起飞机场
     * @param {string} arrAirport - 到达机场
     * @param {number} blockMinutes - 飞行时间（分钟）
     * @param {Array} constraints - 约束列表
     * @param {Object} options - 选项
     * @returns {Object} 包含起飞时间(stdTime)、到达时间(staTime)和调整记录(adjustments)的对象
     */
    static findNextValidTime(initialTime, depAirport, arrAirport, blockMinutes, constraints, options) {
        const {
            airportConstraints,
            airportsMap,
            timeZone,
            aircraft_reg,
            aircraftAvailabilityWindowsByRegistration = {},
            aircraftAvailabilityQueryStart,
            aircraftAvailabilityQueryEnd,
        } = options;

        if (!initialTime || !depAirport || !arrAirport) {
            // 如果参数无效，直接返回初始时间和对应的到达时间
            const stdTime = dayjs.utc(initialTime).toISOString();
            const staTime = dayjs.utc(initialTime).add(blockMinutes, "minute").toISOString();
            return { stdTime, staTime, adjustments: [] };
        }

        // 克隆初始时间防止修改原对象
        let currentDateTime = new Date(initialTime.getTime());
        const originalDateTime = new Date(initialTime.getTime());

        // 调整记录数组
        let adjustments = [];

        // 最大循环次数，防止无限循环
        const MAX_ITERATIONS = 1000;
        let iterations = 0;
        let isValid = false;

        // 递增步长（分钟）
        const STEP_MINUTES = 5;

        while (!isValid && iterations < MAX_ITERATIONS) {
            iterations++;

            // 检查所有约束
            let constraintViolations = [];
            let fatalError = null;

            for (const constraint of constraints) {
                // 跳过禁用的约束
                if (constraint.enabled === false) continue;

                switch (constraint.type) {
                    case "curfew": {
                        // 检查起飞机场宵禁（处理范围 [宵禁开始, 结束+缓冲)，落点 = 结束 + 缓冲的精确值）
                        if (getAirportConstraint(airportConstraints, depAirport)) {
                            const depShift = this.getCurfewBufferShift(currentDateTime, depAirport, "departure", {
                                airportConstraints,
                                aircraft_reg,
                            });

                            if (depShift) {
                                constraintViolations.push({
                                    type: "departure_curfew",
                                    nextTime: depShift.nextTime,
                                    explanation: depShift.inCurfew ? `起飞机场 ${depAirport} 宵禁约束违反` : `起飞机场 ${depAirport} 距宵禁结束不足 ${CURFEW_BUFFER_MINUTES} 分钟`,
                                });
                            }
                        }

                        // 检查到达机场宵禁（同样带缓冲，反推起飞时间）
                        if (getAirportConstraint(airportConstraints, arrAirport)) {
                            // 计算预计到达时间
                            const estimatedArrivalTime = new Date(dayjs.utc(currentDateTime).add(blockMinutes, "minute").toDate());

                            const arrShift = this.getCurfewBufferShift(estimatedArrivalTime, arrAirport, "arrival", {
                                airportConstraints,
                                aircraft_reg,
                            });

                            if (arrShift) {
                                // 从到达落点反推起飞时间
                                const depNextTime = new Date(dayjs.utc(arrShift.nextTime).subtract(blockMinutes, "minute").toDate());

                                constraintViolations.push({
                                    type: "arrival_curfew",
                                    nextTime: depNextTime,
                                    explanation: arrShift.inCurfew ? `到达机场 ${arrAirport} 宵禁约束违反` : `到达机场 ${arrAirport} 距宵禁结束不足 ${CURFEW_BUFFER_MINUTES} 分钟`,
                                });
                            }
                        }
                        break;
                    }

                    case "weekday": {
                        // 检查星期几约束
                        const dayOfWeek = dayjs.utc(currentDateTime).day(); // 0是周日，1-6是周一至周六
                        // 转换成"1-7"表示周一至周日
                        const adjustedDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

                        if (!constraint.allowedDays.includes(adjustedDayOfWeek)) {
                            // 计算下一个允许的日期
                            const nextAllowedDay = this.findNextAllowedDay(adjustedDayOfWeek, constraint.allowedDays);
                            const daysToAdd = nextAllowedDay <= adjustedDayOfWeek ? nextAllowedDay + 7 - adjustedDayOfWeek : nextAllowedDay - adjustedDayOfWeek;

                            const nextTime = new Date(dayjs.utc(currentDateTime).add(daysToAdd, "day").startOf("day").toDate());

                            constraintViolations.push({
                                type: "weekday",
                                nextTime,
                                explanation: `日期 ${dayjs.utc(currentDateTime).format("YYYY-MM-DD")} 不在允许的星期范围内`,
                                detail: `需要调整到下一个合法日期 (星期${["一", "二", "三", "四", "五", "六", "日"][nextAllowedDay - 1]})`,
                            });
                        }
                        break;
                    }

                    case "preferredTime": {
                        // 如果是同一天但时间已过，尝试使用首选时间
                        if (constraint.preferredTime && constraint.origin === depAirport) {
                            // 获取当前时间的小时和分钟
                            const currentHour = dayjs.utc(currentDateTime).hour();
                            const currentMinute = dayjs.utc(currentDateTime).minute();

                            // 获取首选时间的小时和分钟
                            const [prefHour, prefMinute] = constraint.preferredTime.split(":").map(Number);

                            // 获取当前日期
                            const currentDate = dayjs.utc(currentDateTime).format("YYYY-MM-DD");

                            // 创建首选时间datetime对象
                            const preferredDateTime = dayjs.utc(`${currentDate}T${constraint.preferredTime}:00Z`);

                            // 如果当前时间不等于首选时间
                            if (currentHour !== prefHour || currentMinute !== prefMinute) {
                                // 如果当前时间早于首选时间，使用今天的首选时间
                                if (currentHour < prefHour || (currentHour === prefHour && currentMinute < prefMinute)) {
                                    constraintViolations.push({
                                        type: "preferredTime",
                                        nextTime: preferredDateTime.toDate(),
                                        explanation: `使用首选时间 ${constraint.preferredTime}`,
                                        detail: `根据运输需求调整到首选时间 ${constraint.preferredTime}`,
                                    });
                                }
                                // 如果当前时间晚于首选时间，检查是否允许使用其他时间
                                // 这里不添加违反，允许使用当前时间
                            }
                        }
                        break;
                    }

                    case "aircraftAvailability": {
                        const candidateStart = dayjs.utc(currentDateTime);
                        const candidateEnd = candidateStart.add(blockMinutes, "minute");
                        const queryStart = dayjs.utc(aircraftAvailabilityQueryStart);
                        const queryEnd = dayjs.utc(aircraftAvailabilityQueryEnd);
                        if (
                            !aircraftAvailabilityQueryStart
                            || !aircraftAvailabilityQueryEnd
                            || !queryStart.isValid()
                            || !queryEnd.isValid()
                            || candidateStart.isBefore(queryStart)
                            || candidateEnd.isAfter(queryEnd)
                        ) {
                            fatalError = {
                                code: "AIRCRAFT_AVAILABILITY_RANGE_INCOMPLETE",
                                message: "正在继续确认飞机在调整后时间段是否可用。",
                                aircraftReg: normalizeAircraftRegistration(aircraft_reg),
                                candidateStart: candidateStart.toISOString(),
                                candidateEnd: candidateEnd.toISOString(),
                            };
                            break;
                        }

                        const overlaps = evaluateAircraftAvailability({
                            flightStart: candidateStart,
                            flightEnd: candidateEnd,
                            windows: getAircraftAvailabilityWindows(
                                aircraftAvailabilityWindowsByRegistration,
                                aircraft_reg,
                            ),
                        }).filter((result) => result.relation === "overlap");
                        const indefiniteWindow = overlaps.find((result) => !result.windowEnd);
                        if (indefiniteWindow) {
                            fatalError = {
                                code: "AIRCRAFT_AVAILABILITY_INDEFINITE",
                                message: "飞机存在无结束时间的不可用窗口，无法自动确定下一次可用时间。请更换飞机、修改窗口或手工安排。",
                                aircraftReg: normalizeAircraftRegistration(aircraft_reg),
                                windowId: indefiniteWindow.window?.id || indefiniteWindow.window?.window_id || null,
                            };
                            break;
                        }
                        overlaps.forEach((overlap) => {
                            constraintViolations.push({
                                type: "aircraft_availability",
                                nextTime: overlap.windowEnd.toDate(),
                                explanation: `飞机 ${normalizeAircraftRegistration(aircraft_reg)} 在候选航班时段内不可用`,
                                detail: "航班已顺延到飞机不可用窗口结束后",
                            });
                        });
                        break;
                    }
                }
                if (fatalError) break;
            }

            if (fatalError) {
                return { error: fatalError, adjustments: [] };
            }

            // 如果没有约束违反，时间是有效的
            if (constraintViolations.length === 0) {
                isValid = true;
            } else {
                // 找到下一个尝试时间（取所有违反中最晚的时间）
                let nextTime = null;
                let selectedViolation = null;

                for (const violation of constraintViolations) {
                    if (violation.nextTime) {
                        if (!nextTime || violation.nextTime > nextTime) {
                            nextTime = violation.nextTime;
                            selectedViolation = violation;
                        }
                    }
                }

                // 如果没有明确的下一个时间点，增加一个小的步长
                if (!nextTime) {
                    nextTime = new Date(dayjs.utc(currentDateTime).add(STEP_MINUTES, "minute").toDate());
                    selectedViolation = {
                        type: "unknown",
                        explanation: "未知约束，增加5分钟",
                        detail: "增加5分钟步长尝试下一个时间点",
                    };
                }

                // 记录时间调整
                if (selectedViolation) {
                    // 计算时间差（分钟）
                    const prevTime = currentDateTime;
                    const timeDiffMinutes = Math.round((nextTime.getTime() - prevTime.getTime()) / (1000 * 60));

                    // 小时和分钟表示
                    const hours = Math.floor(timeDiffMinutes / 60);
                    const minutes = timeDiffMinutes % 60;
                    let timeDesc = "";

                    if (hours > 0 && minutes > 0) {
                        timeDesc = `${hours}小时${minutes}分钟`;
                    } else if (hours > 0) {
                        timeDesc = `${hours}小时`;
                    } else {
                        timeDesc = `${minutes}分钟`;
                    }

                    // 添加到调整记录
                    adjustments.push({
                        type: selectedViolation.type,
                        reason: selectedViolation.explanation,
                        detail: selectedViolation.detail || selectedViolation.explanation,
                        timeDiff: timeDiffMinutes,
                        timeDesc: timeDesc,
                        from: dayjs.utc(prevTime).format("YYYY-MM-DD HH:mm"),
                        to: dayjs.utc(nextTime).format("YYYY-MM-DD HH:mm"),
                    });
                }
                currentDateTime = nextTime;
            }
        }

        // 如果达到最大迭代次数还没找到，返回原始时间
        if (iterations >= MAX_ITERATIONS) {
            console.warn(`查找可用时间达到最大迭代次数(${MAX_ITERATIONS})，无法找到满足所有约束的时间`);
            const stdTime = dayjs.utc(initialTime).toISOString();
            const staTime = dayjs.utc(initialTime).add(blockMinutes, "minute").toISOString();
            return { stdTime, staTime, adjustments: [] };
        }

        // 检查最终时间与初始时间是否有差异
        const totalMinutesDiff = Math.round((currentDateTime.getTime() - originalDateTime.getTime()) / (1000 * 60));

        // 如果有时间差但没有详细记录（可能是一步调整完成），添加一个总记录
        if (totalMinutesDiff > 0 && adjustments.length === 0) {
            const hours = Math.floor(totalMinutesDiff / 60);
            const minutes = totalMinutesDiff % 60;
            let timeDesc = "";

            if (hours > 0 && minutes > 0) {
                timeDesc = `${hours}小时${minutes}分钟`;
            } else if (hours > 0) {
                timeDesc = `${hours}小时`;
            } else {
                timeDesc = `${minutes}分钟`;
            }

            adjustments.push({
                type: "total_adjustment",
                reason: "综合约束调整",
                detail: "根据多种约束条件进行的时间调整",
                timeDiff: totalMinutesDiff,
                timeDesc: timeDesc,
                from: dayjs.utc(originalDateTime).format("YYYY-MM-DD HH:mm"),
                to: dayjs.utc(currentDateTime).format("YYYY-MM-DD HH:mm"),
            });
        }

        // 计算满足所有约束的起飞时间和到达时间
        const stdTime = dayjs.utc(currentDateTime).toISOString();
        const staTime = dayjs.utc(currentDateTime).add(blockMinutes, "minute").toISOString();

        return { stdTime, staTime, adjustments };
    }

    /**
     * 带缓冲的宵禁判定：时间落在 [宵禁开始, 宵禁结束 + 缓冲) 内时返回落点信息，否则返回 null。
     *
     * 该判定只在本服务内部使用，不改动共用的 ConstraintsService.checkCurfewViolation
     * （监控页等共用方的违规边界保持 [开始, 结束) 不变）。
     * 缓冲带方向的规则适用性跟随本端违规判定口径（按检查时刻所在天）。
     *
     * @param {Date|string} dateTime - 需要检查的时间
     * @param {string} airportCode - 机场代码
     * @param {string} operation - 'departure' 或 'arrival'
     * @param {Object} options - { airportConstraints, aircraft_reg }
     * @returns {Object|null} { nextTime: Date（结束 + 缓冲的精确落点）, inCurfew: boolean（true=在宵禁内，false=在缓冲带内） }
     */
    static getCurfewBufferShift(dateTime, airportCode, operation, options = {}) {
        const { airportConstraints, aircraft_reg } = options;
        const airportData = getAirportConstraint(airportConstraints, airportCode);
        if (!dateTime || !airportData) {
            return null;
        }

        // 1) 宵禁窗口内：落点 = 宵禁结束 + 缓冲
        const violation = ConstraintsService.checkCurfewViolation(dateTime, airportCode, operation, {
            airportConstraints,
            aircraft_reg,
        });
        if (violation.isViolated && violation.nextAvailableTime) {
            return {
                nextTime: dayjs.utc(violation.nextAvailableTime).add(CURFEW_BUFFER_MINUTES, "minute").toDate(),
                inCurfew: true,
            };
        }

        // 2) 缓冲带内（[结束, 结束+缓冲)）：合法但重算要挪，落点同为结束 + 缓冲
        const rules = operation === "departure" ? airportData.dep_curfew_rules : operation === "arrival" ? airportData.arr_curfew_rules : [];
        if (!rules || rules.length === 0) {
            return null;
        }

        const timeObj = dayjs.utc(dateTime);
        if (!timeObj.isValid()) {
            return null;
        }
        const timeHHmm = timeObj.format("HH:mm");

        let latestTarget = null;
        for (const rule of rules) {
            if (!ConstraintsService.isCurfewRuleApplicableOnDate(rule, timeObj, aircraft_reg)) {
                continue;
            }
            const minutesFromCurfewEnd = timeUtils.getMinutesFromCurfewEnd(timeHHmm, rule);
            if (minutesFromCurfewEnd === null) {
                continue;
            }
            // 先回退到结束时刻（处理跨零点回绕），再加缓冲得到落点
            const target = timeObj.subtract(minutesFromCurfewEnd, "minute").add(CURFEW_BUFFER_MINUTES, "minute");
            if (!latestTarget || target.isAfter(latestTarget)) {
                latestTarget = target;
            }
        }

        return latestTarget ? { nextTime: latestTarget.toDate(), inCurfew: false } : null;
    }

    /**
     * 判断一个航班的原时间（STD/STA）是否落在宵禁或结束后缓冲带内
     * @param {Object} flight - 航班对象
     * @param {Object} airportConstraints - 机场约束
     * @returns {boolean}
     */
    static isFlightTimeInCurfewOrBuffer(flight, airportConstraints) {
        if (!flight) {
            return false;
        }
        const options = { airportConstraints, aircraft_reg: flight.aircraft_reg };
        if (flight.std && flight.departure_airport && this.getCurfewBufferShift(flight.std, flight.departure_airport, "departure", options)) {
            return true;
        }
        if (flight.sta && flight.arrival_airport && this.getCurfewBufferShift(flight.sta, flight.arrival_airport, "arrival", options)) {
            return true;
        }
        return false;
    }

    /**
     * 向链条后方扫描：是否还有原时间落在宵禁或缓冲带内的航班。
     * 旧版列表多机混排，只扫描与当前航班同一 aircraft_reg 的后续航班，
     * 避免被别的飞机的冲突拖住不停。
     * @param {Array} flights - 当前计算中的航班列表（按页面顺序）
     * @param {Array} originalFlights - 原始航班列表（重算前的快照）
     * @param {number} startIndex - 从哪个下标开始向后扫描
     * @param {string} aircraftReg - 当前航班的飞机注册号
     * @param {Object} airportConstraints - 机场约束
     * @param {number} [scanEndIndex] - 可选；扫描到该下标（含）为止，用于「重算选中」只看到选中段末尾
     * @returns {boolean}
     */
    static hasDownstreamCurfewOrBufferConflict(flights, originalFlights, startIndex, aircraftReg, airportConstraints, scanEndIndex = null) {
        const lastScan = scanEndIndex == null ? flights.length - 1 : Math.min(scanEndIndex, flights.length - 1);
        for (let j = startIndex; j <= lastScan; j++) {
            const flight = flights[j];
            if (flight.aircraft_reg !== aircraftReg) {
                continue;
            }
            const originalFlight = (originalFlights && originalFlights.find((f) => f.id === flight.id)) || flight;
            if (this.isFlightTimeInCurfewOrBuffer(originalFlight, airportConstraints)) {
                return true;
            }
        }
        return false;
    }

    static isFlightInAircraftAvailability(flight, windowsByRegistration = {}) {
        if (!flight?.std || !flight?.sta || !flight?.aircraft_reg) {
            return false;
        }
        return evaluateAircraftAvailability({
            flightStart: flight.std,
            flightEnd: flight.sta,
            windows: getAircraftAvailabilityWindows(windowsByRegistration, flight.aircraft_reg),
        }).some((result) => result.relation === "overlap");
    }

    static hasDownstreamAircraftAvailabilityConflict(
        flights,
        originalFlights,
        startIndex,
        aircraftReg,
        windowsByRegistration = {},
        scanEndIndex = null,
    ) {
        const lastScan = scanEndIndex == null ? flights.length - 1 : Math.min(scanEndIndex, flights.length - 1);
        for (let index = startIndex; index <= lastScan; index += 1) {
            const flight = flights[index];
            if (normalizeAircraftRegistration(flight?.aircraft_reg) !== normalizeAircraftRegistration(aircraftReg)) {
                continue;
            }
            const originalFlight = originalFlights?.find((item) => item.id === flight.id) || flight;
            if (this.isFlightInAircraftAvailability(originalFlight, windowsByRegistration)) {
                return true;
            }
        }
        return false;
    }

    /**
     * 查找下一个允许的星期几
     * @param {number} currentDay - 当前星期几 (1-7，表示周一至周日)
     * @param {Array} allowedDays - 允许的星期几数组
     * @returns {number} 下一个允许的星期几
     */
    static findNextAllowedDay(currentDay, allowedDays) {
        if (!allowedDays || allowedDays.length === 0) {
            return currentDay; // 如果没有限制，返回当前天
        }

        // 确保allowedDays按升序排序
        const sortedAllowedDays = [...allowedDays].sort((a, b) => a - b);

        // 查找下一个允许的日期
        const nextDay = sortedAllowedDays.find((day) => day > currentDay);

        // 如果找不到大于当前日期的，则返回最小的允许日期（即下周）
        return nextDay || sortedAllowedDays[0];
    }

    /**
     * 计算星期几
     * @param {string} dateStr - ISO格式的日期字符串
     * @returns {number} 星期几 (1-7 表示周一至周日)
     */
    static getDayOfWeek(dateStr) {
        if (!dateStr) return "";

        try {
            // 解析日期 - 保持UTC时区
            const dayjsObj = dayjs.utc(dateStr);

            // 如果日期无效，返回空
            if (!dayjsObj.isValid()) {
                console.error("无效的日期格式:", dateStr);
                return "";
            }

            const day = dayjsObj.day();
            // 转换为1-7 (周一-周日)，dayjs中0是周日
            return day === 0 ? 7 : day;
        } catch (error) {
            console.error("计算星期错误:", error, "原始日期:", dateStr);
            return "";
        }
    }

    /**
     * 重算写回页面：起飞/到达没变的班，保持页面上的轮档和到达一对。
     * 避免重算前拉到的新轮档写到锚点班、追平班上，把两列拆开。
     */
    static mergeCalculatedFlightsKeepingUnchangedPairs(pageFlights, calculatedFlights) {
        const calculatedMap = new Map((calculatedFlights || []).map((flight) => [flight.id, flight]));
        return (pageFlights || []).map((pageFlight) => {
            const calculated = calculatedMap.get(pageFlight.id);
            if (!calculated) return pageFlight;
            const sameStd = truncateIsoToMinute(calculated.std) === truncateIsoToMinute(pageFlight.std);
            const sameSta = truncateIsoToMinute(calculated.sta) === truncateIsoToMinute(pageFlight.sta);
            if (sameStd && sameSta) return pageFlight;
            return calculated;
        });
    }
}

export default TimeCalculationService;
